/** Поля протокола амбулаторного осмотра (привязаны к записи / визиту). */
import { icd10DiagnosisTypeLabel } from 'constants/icd10';
import {
  ambulatorySpecialtyStatusLabel,
  resolveAmbulatorySpecialty,
} from 'constants/ambulatorySpecialty';

export function createEmptyDiagnosis(type = 'clinical') {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    code: '',
    type,
    text: '',
  };
}

export function normalizeDiagnosesList(source) {
  const raw = source?.diagnoses;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((item, index) => ({
      id: item?.id || `diag-${index}-${item?.code || 'x'}`,
      code: String(item?.code ?? '').trim(),
      type: String(item?.type ?? 'clinical').trim() || 'clinical',
      text: String(item?.text ?? '').trim(),
    }));
  }

  const legacyCode = source?.diagnosisIcd10Code ?? source?.diagnosis_icd10_code ?? '';
  const legacyType = source?.diagnosisIcd10Type ?? source?.diagnosis_icd10_type ?? 'clinical';
  const legacyText = source?.diagnosis ?? '';
  if (legacyCode || legacyText) {
    return [
      {
        id: 'legacy-0',
        code: String(legacyCode).trim(),
        type: String(legacyType).trim() || 'clinical',
        text: String(legacyText).trim(),
      },
    ];
  }

  return [createEmptyDiagnosis('clinical')];
}

export function diagnosesToSummaryText(diagnoses) {
  const list = normalizeDiagnosesList({ diagnoses }).filter(
    (d) => d.code || d.text
  );
  if (!list.length) return '';
  return list
    .map((d, index) => {
      const line = d.text || d.code;
      if (index === 0 && d.type === 'clinical') {
        return line;
      }
      const typeLabel = icd10DiagnosisTypeLabel(d.type);
      return typeLabel ? `${typeLabel}: ${line}` : line;
    })
    .join('; ');
}

/** Клинический и сопутствующие диагнозы для печати амбулаторной карты. */
export function ambulatoryDiagnosisPrintParts(source) {
  const p = ambulatoryProtocolFromSource(source);
  const list = normalizeDiagnosesList(p).filter((d) => d.code || d.text);

  const clinicalItem =
    list.find((d) => d.type === 'clinical') || list.find((d) => d.type !== 'comorbid') || list[0];
  const clinical = clinicalItem ? String(clinicalItem.text || clinicalItem.code).trim() : '';
  const clinicalFallback = String(p.diagnosis || '').trim();

  const concomitantLines = list
    .filter((d) => d.type === 'comorbid' && d !== clinicalItem)
    .map((d) => String(d.text || d.code).trim())
    .filter(Boolean);

  return {
    clinical: clinical || (concomitantLines.length ? '' : clinicalFallback),
    concomitant: concomitantLines.join('\n'),
  };
}

export const EMPTY_PROTOCOL_META = {
  equipment: '',
  caseNumber: '',
  department: '',
  anesthesia: '',
};

export function normalizeProtocolMeta(raw) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    equipment: String(source.equipment ?? '').trim(),
    caseNumber: String(source.caseNumber ?? source.case_number ?? '').trim(),
    department: String(source.department ?? '').trim(),
    anesthesia: String(source.anesthesia ?? '').trim(),
  };
}

export const EMPTY_AMBULATORY_PROTOCOL = {
  protocolExamination: '',
  protocolComplaints: '',
  protocolAnamnesisLife: '',
  protocolAnamnesisDisease: '',
  protocolObjectiveStatus: '',
  protocolLocalStatus: '',
  protocolSpecialtyStatus: '',
  protocolSpecialty: '',
  protocolMeta: { ...EMPTY_PROTOCOL_META },
  diagnosis: '',
  diagnosisIcd10Code: '',
  diagnosisIcd10Type: 'clinical',
  diagnoses: [createEmptyDiagnosis('clinical')],
  recommendations: '',
  protocolPostExamComments: '',
};

export function ambulatoryProtocolFromSource(source) {
  if (!source) return { ...EMPTY_AMBULATORY_PROTOCOL };
  const diagnoses = normalizeDiagnosesList(source);
  const first = diagnoses[0] || createEmptyDiagnosis('clinical');
  return {
    protocolExamination:
      source.protocolExamination ?? source.protocol_examination ?? '',
    protocolComplaints: source.protocolComplaints ?? source.protocol_complaints ?? '',
    protocolAnamnesisLife:
      source.protocolAnamnesisLife ?? source.protocol_anamnesis_life ?? '',
    protocolAnamnesisDisease:
      source.protocolAnamnesisDisease ?? source.protocol_anamnesis_disease ?? '',
    protocolObjectiveStatus:
      source.protocolObjectiveStatus ?? source.protocol_objective_status ?? '',
    protocolLocalStatus: source.protocolLocalStatus ?? source.protocol_local_status ?? '',
    protocolSpecialtyStatus:
      source.protocolSpecialtyStatus ?? source.protocol_specialty_status ?? '',
    protocolSpecialty: source.protocolSpecialty ?? source.protocol_specialty ?? '',
    protocolMeta: normalizeProtocolMeta(source.protocolMeta ?? source.protocol_meta),
    diagnosis: source.diagnosis ?? diagnosesToSummaryText(diagnoses) ?? '',
    diagnosisIcd10Code: first.code,
    diagnosisIcd10Type: first.type,
    diagnoses,
    recommendations: source.recommendations ?? '',
    protocolPostExamComments:
      source.protocolPostExamComments ?? source.protocol_post_exam_comments ?? '',
  };
}

function trimOrNull(value) {
  const s = String(value ?? '').trim();
  return s === '' ? null : s;
}

/** Payload для bookingsApi.update */
export function ambulatoryProtocolToApiPayload(draft, options = {}) {
  const diagnoses = normalizeDiagnosesList(draft).filter((d) => d.code || d.text);
  const summary = diagnosesToSummaryText(diagnoses);
  const serviceName = options.serviceName || '';
  const doctorName = options.doctorName || '';
  const protocolSpecialty =
    draft.protocolSpecialty ||
    resolveAmbulatorySpecialty(serviceName, draft.protocolSpecialty, doctorName).id;

  return {
    protocolExamination: trimOrNull(draft.protocolExamination),
    protocolComplaints: trimOrNull(draft.protocolComplaints),
    protocolAnamnesisLife: trimOrNull(draft.protocolAnamnesisLife),
    protocolAnamnesisDisease: trimOrNull(draft.protocolAnamnesisDisease),
    protocolObjectiveStatus: trimOrNull(draft.protocolObjectiveStatus),
    protocolLocalStatus: trimOrNull(draft.protocolLocalStatus),
    protocolSpecialtyStatus: trimOrNull(draft.protocolSpecialtyStatus),
    protocolSpecialty: trimOrNull(protocolSpecialty),
    protocolMeta: normalizeProtocolMeta(draft.protocolMeta),
    diagnosis: trimOrNull(summary || draft.diagnosis),
    diagnoses: diagnoses.map(({ code, type, text }) => ({ code, type, text })),
    recommendations: trimOrNull(draft.recommendations),
    protocolPostExamComments: trimOrNull(draft.protocolPostExamComments),
  };
}

export function mergeAmbulatoryProtocolVisit(visit, draft) {
  if (!visit) return visit;
  if (!draft) return visit;
  return {
    ...visit,
    ...draft,
  };
}

/** Краткая строка для списка визитов */
export function protocolHasSavedContent(source) {
  const p = ambulatoryProtocolFromSource(source);
  const diagnosisLine = diagnosesToSummaryText(p.diagnoses) || p.diagnosis;
  return Boolean(
    String(p.protocolExamination || '').trim() ||
      String(p.protocolComplaints || '').trim() ||
      String(p.protocolAnamnesisLife || '').trim() ||
      String(p.protocolAnamnesisDisease || '').trim() ||
      String(p.protocolObjectiveStatus || '').trim() ||
      String(p.protocolLocalStatus || '').trim() ||
      String(p.protocolSpecialtyStatus || '').trim() ||
      Object.values(normalizeProtocolMeta(p.protocolMeta)).some(Boolean) ||
      String(diagnosisLine || '').trim() ||
      String(p.recommendations || '').trim() ||
      String(p.protocolPostExamComments || '').trim()
  );
}

/** Секции «Рекомендации» / «К лечению» для печати и предпросмотра. */
export function ambulatoryTreatmentPrintSections(protocol) {
  const p = ambulatoryProtocolFromSource(protocol);
  const recommendations = String(p.recommendations || '').trim();
  const treatment = String(p.protocolPostExamComments || '').trim();
  const sections = [];

  if (recommendations) {
    sections.push({ label: 'Рекомендации', value: recommendations });
  }
  if (treatment) {
    sections.push({ label: 'К лечению', value: treatment });
  }

  return sections;
}

export function ambulatoryProtocolSummary(protocol) {
  const p = ambulatoryProtocolFromSource(protocol);
  const diagnosisLine = diagnosesToSummaryText(p.diagnoses) || p.diagnosis;
  const parts = [
    p.protocolComplaints && `Жалобы: ${p.protocolComplaints}`,
    diagnosisLine && `Диагноз: ${diagnosisLine}`,
  ].filter(Boolean);
  return parts.join(' · ') || 'Протокол не заполнен';
}

/** Текст для блока «Эскертме» на каттоо картасы — только из полей протокола осмотра. */
export function buildAmbulatoryCardNoteFromProtocol(protocol, serviceName = '', doctorName = '') {
  const p = ambulatoryProtocolFromSource(protocol);
  const { clinical, concomitant } = ambulatoryDiagnosisPrintParts(p);
  const specialty = resolveAmbulatorySpecialty(
    serviceName || protocol?.serviceName,
    p.protocolSpecialty,
    doctorName
  );

  const sections = [
    ['Жалобы', p.protocolComplaints],
    ['Анамнез болезни', p.protocolAnamnesisDisease],
    ['Анамнез жизни', p.protocolAnamnesisLife],
    ['Объективный статус', p.protocolObjectiveStatus],
    ...(specialty.id === 'general'
      ? [['Локальный статус', p.protocolLocalStatus]]
      : [[specialty.statusLabel, p.protocolSpecialtyStatus || p.protocolLocalStatus]]),
    ['Диагноз', clinical],
    ...(concomitant ? [['Сопутствующий диагноз', concomitant]] : []),
    ...ambulatoryTreatmentPrintSections(p).map(({ label, value }) => [label, value]),
  ];

  return sections
    .map(([label, value]) => {
      const text = String(value || '').trim();
      return text ? `${label}: ${text}` : '';
    })
    .filter(Boolean)
    .join('\n');
}
